import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DNSRecordsDisplay from './DNSRecordsDisplay';
import { validateDomain } from '../../lib/domainValidation';
import { supabase } from '../../lib/supabase';

const STEPS = [
  {
    id: 'domain',
    badge: 'Start',
    badgeColor: 'text-[#10b981] bg-[#10b981]/10',
    title: 'Enter your',
    highlight: 'Domain',
    highlightClass: 'bg-gradient-to-r from-[#a855f7] to-[#7c6ee8] bg-clip-text text-transparent',
    subtitle: 'Your custom domain (e.g. mybrand.com).',
  },
  {
    id: 'rootRedirect',
    badge: 'Redirect',
    badgeColor: 'text-orange-500 bg-orange-500/10',
    title: 'Root',
    highlight: 'Redirect',
    highlightClass: 'text-orange-500',
    subtitle: 'Optional. Where to send visitors who open the domain without a slug or with an undefined slug.',
  },
  {
    id: 'dns',
    badge: 'DNS',
    badgeColor: 'text-[#135bec] bg-[#135bec]/10',
    title: 'DNS',
    highlight: 'Setup',
    highlightClass: 'bg-gradient-to-r from-[#135bec] to-[#42a5f5] bg-clip-text text-transparent',
    subtitle: 'Add these records at your domain registrar.',
  },
  {
    id: 'verify',
    badge: 'Verify',
    badgeColor: 'text-yellow-500 bg-yellow-500/10',
    title: 'Verify',
    highlight: 'Configuration',
    highlightClass: 'text-yellow-500',
    subtitle: 'Confirm DNS is set up correctly. May take a few minutes after adding records.',
  },
];

export default function DomainWizardOnePerPage({
  domainName,
  rootRedirect,
  onDomainNameChange,
  onRootRedirectChange,
  dnsRecords,
  onRegister,
  onVerify,
  onRefreshDns,
  onBack,
  isSubmitting,
  isVerifying,
  saveError,
  verifyError,
  rootRedirectError,
  isEdit,
}) {
  const steps = isEdit ? [STEPS[1]] : STEPS;
  const [stepIndex, setStepIndex] = useState(0);
  const [localError, setLocalError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const domainInputRef = useRef(null);
  const rootRedirectInputRef = useRef(null);

  useEffect(() => {
    setStepIndex(0);
  }, [isEdit]);

  const totalSteps = steps.length;
  const currentStep = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const progressPct = totalSteps ? ((stepIndex + 1) / totalSteps) * 100 : 0;
  const validDnsRecordsCount = Array.isArray(dnsRecords)
    ? dnsRecords.filter((r) => r && (r.host || r.name) && r.value).length
    : 0;
  const hasAllRequiredDnsRecords = validDnsRecordsCount >= 6;

  useEffect(() => {
    if (!currentStep) return;
    const t = setTimeout(() => {
      if (currentStep.id === 'domain' && !isEdit) {
        domainInputRef.current?.focus?.();
        domainInputRef.current?.select?.();
        return;
      }
      if (currentStep.id === 'rootRedirect') {
        rootRedirectInputRef.current?.focus?.();
        rootRedirectInputRef.current?.select?.();
      }
    }, 0);
    return () => clearTimeout(t);
  }, [currentStep?.id, isEdit]);

  const goNext = async () => {
    if (currentStep.id === 'domain') {
      const validation = validateDomain(domainName || '', {
        allowSubdomains: true,
        allowPunycode: true,
        requireTLD: true,
        allowLocalhost: false,
        allowIP: false,
      });

      if (!validation.isValid) {
        setFieldErrors((prev) => ({ ...prev, domain: validation.error || 'Please enter a valid domain.' }));
        return;
      }

      const finalDomain = (validation.sanitized || '').trim().toLowerCase();
      if (!finalDomain) {
        setFieldErrors((prev) => ({ ...prev, domain: 'Please enter a valid domain.' }));
        return;
      }

      // Check if this domain is already added for this user (first page only; Root Redirect is not checked)
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data: existing } = await supabase
          .from('custom_domains')
          .select('id')
          .eq('user_id', user.id)
          .ilike('domain', finalDomain);
        if (existing?.length > 0) {
          setFieldErrors((prev) => ({
            ...prev,
            domain: 'This domain is already added. Use a different domain or edit the existing one.',
          }));
          return;
        }
      }

      // Normalize domain display early so user sees the exact validated value.
      if (validation.sanitized && validation.sanitized !== domainName) {
        onDomainNameChange?.(validation.sanitized);
      }

      setFieldErrors((prev) => ({ ...prev, domain: null }));
      setStepIndex(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (currentStep.id === 'rootRedirect') {
      if (rootRedirectError) {
        setFieldErrors((prev) => ({
          ...prev,
          rootRedirect: rootRedirectError || 'Please enter a valid redirect URL.',
        }));
        return;
      }
      setFieldErrors((prev) => ({ ...prev, rootRedirect: null }));
      setLocalError(null);
      try {
        await onRegister(domainName, rootRedirect);
        if (!isEdit) {
          setStepIndex(2);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (e) {
        setLocalError(e.message || 'Could not register the domain. Please try again.');
      }
      return;
    }
    if (currentStep.id === 'dns') {
      setStepIndex(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (currentStep.id === 'verify') {
      // Allow finishing even if DNS is still pending.
      onBack?.();
      return;
    }
  };

  const goBack = () => {
    if (isEdit) {
      onBack?.();
      return;
    }
    if (!isFirst) {
      setStepIndex((i) => i - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      onBack?.();
    }
  };

  const errorToShow = localError || saveError;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div
        className="h-1 bg-slate-200 flex-shrink-0"
        role="progressbar"
        aria-valuenow={stepIndex + 1}
        aria-valuemin={1}
        aria-valuemax={totalSteps}
        aria-label={`Step ${stepIndex + 1} of ${totalSteps}`}
      >
        <div
          className="h-full bg-[#135bec] transition-all duration-500 shadow-[0_0_10px_#135bec]"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#135bec] opacity-[0.04] blur-[150px] pointer-events-none"
        aria-hidden="true"
      />
      <div className="flex-1 min-h-0 flex flex-col justify-center px-6 pb-32 pt-8 max-w-2xl mx-auto w-full relative z-10">
        {((errorToShow && currentStep.id !== 'rootRedirect') || verifyError) && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 text-red-600 rounded-xl text-sm" role="alert">
            {currentStep.id === 'verify' ? verifyError : errorToShow}
          </div>
        )}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep?.id}
            initial={{ opacity: 0, transform: 'translateY(10px)' }}
            animate={{ opacity: 1, transform: 'translateY(0)' }}
            exit={{ opacity: 0, transform: 'translateY(-10px)' }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="space-y-3">
              <div className="flex justify-between items-center w-full">
                <div
                  className={`inline-flex items-center gap-2 font-bold text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full ${currentStep?.badgeColor}`}
                >
                  <span>{currentStep?.badge}</span>
                </div>
                <span className="text-[10px] font-black text-gray-500 tracking-widest">
                  {stepIndex + 1}/{totalSteps}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-[#1b1b1b]">
                {currentStep?.title}{' '}
                <span
                  className={
                    currentStep?.highlightClass ||
                    'bg-gradient-to-r from-[#a855f7] to-[#7c6ee8] bg-clip-text text-transparent'
                  }
                >
                  {currentStep?.highlight}
                </span>
              </h1>
              <p className="text-gray-500 font-medium text-2xl">{currentStep?.subtitle}</p>
            </div>

            {currentStep?.id === 'domain' && (
              <div className="space-y-5">
                <div className="rounded-2xl bg-white border-2 border-slate-200 focus-within:border-[#135bec] transition-all">
                  <input
                    ref={domainInputRef}
                    type="text"
                    value={domainName}
                    onChange={(e) => {
                      onDomainNameChange?.(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, domain: null }));
                    }}
                    placeholder="mybrand.com"
                    disabled={!!isEdit}
                    aria-label="Domain name"
                    aria-describedby={fieldErrors.domain ? 'domain-error' : undefined}
                    className="w-full bg-transparent py-5 px-6 text-xl outline-none border-none text-[#1b1b1b] placeholder-slate-500 disabled:opacity-60"
                  />
                </div>
                <p className="text-slate-500 text-xs mt-1">
                  Use the exact hostname you want (e.g. tipul.com or www.tipul.com).
                </p>
                <div className="min-h-[20px] mt-1">
                  {fieldErrors.domain && (
                    <p id="domain-error" className="text-red-500 text-sm" role="alert">{fieldErrors.domain}</p>
                  )}
                </div>
              </div>
            )}

            {currentStep?.id === 'rootRedirect' && (
              <div className="space-y-5">
                <div className="rounded-2xl bg-white border-2 border-slate-200 focus-within:border-[#135bec] transition-all">
                  <input
                    ref={rootRedirectInputRef}
                    type="text"
                    value={rootRedirect}
                    onChange={(e) => {
                      onRootRedirectChange?.(e.target.value.toLowerCase());
                      setFieldErrors((prev) => ({ ...prev, rootRedirect: null }));
                    }}
                    placeholder="https://example.com"
                    aria-label="Root redirect URL"
                    aria-describedby={fieldErrors.rootRedirect || rootRedirectError ? 'root-redirect-error' : undefined}
                    className="w-full bg-transparent py-5 px-6 text-xl outline-none border-none text-[#1b1b1b] placeholder-slate-500"
                    autoFocus
                  />
                </div>
                <p className="text-slate-500 text-xs mt-1">
                  Leave empty if you don’t need a redirect for the root URL.
                </p>
                <div className="min-h-[20px] mt-1">
                  {(fieldErrors.rootRedirect || rootRedirectError) && (
                    <p id="root-redirect-error" className="text-red-500 text-sm" role="alert">
                      {fieldErrors.rootRedirect || rootRedirectError}
                    </p>
                  )}
                </div>
              </div>
            )}

            {currentStep?.id === 'dns' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  {!hasAllRequiredDnsRecords && (
                    <button
                      type="button"
                      onClick={onRefreshDns}
                      disabled={isSubmitting}
                      className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-white text-[#1b1b1b] text-sm font-medium rounded-xl transition-colors border border-slate-300 hover:border-[#a855f7]"
                    >
                      <span
                        className={`material-symbols-outlined text-lg ${isSubmitting ? 'animate-spin' : ''}`}
                        aria-hidden="true"
                      >
                        refresh
                      </span>
                      Refresh Records
                    </button>
                  )}
                </div>
                {dnsRecords && dnsRecords.length > 0 ? (
                  <DNSRecordsDisplay records={dnsRecords} domain={domainName} />
                ) : (
                  <div className="rounded-2xl bg-white border border-slate-200 p-6 text-[#1b1b1b] text-sm">
                    Almost done — please complete the previous step and then continue.
                  </div>
                )}
              </div>
            )}

            {currentStep?.id === 'verify' && (
              <div className="space-y-6">
                <p className="text-[#1b1b1b] text-sm">
                  Click the button below to verify your DNS configuration. Propagation can take a
                  few minutes after adding records.
                </p>
                <button
                  type="button"
                  onClick={onVerify}
                  disabled={isVerifying}
                  className="w-full flex items-center justify-center gap-3 py-5 rounded-2xl font-bold text-lg bg-[#10b981] hover:bg-[#0d9668] text-[#1b1b1b] disabled:opacity-60 disabled:cursor-not-allowed transition-all border-2 border-[#10b981]/30"
                >
                  {isVerifying ? (
                    <>
                      <span className="material-symbols-outlined animate-spin text-2xl" aria-hidden="true">
                        refresh
                      </span>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-2xl" aria-hidden="true">verified</span>
                      Verify DNS Records
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <footer className="fixed bottom-0 left-0 right-0 p-6 bg-white/95 backdrop-blur border-t border-slate-200 z-50">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            type="button"
            onClick={goBack}
            aria-label="Previous step"
            className={`flex items-center justify-center p-5 rounded-2xl border border-slate-200 font-bold text-gray-400 hover:bg-slate-200 hover:text-[#1b1b1b] transition-all ${isFirst ? 'invisible' : ''}`}
          >
            <span className="material-symbols-outlined text-2xl" aria-hidden="true">chevron_left</span>
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={currentStep?.id === 'rootRedirect' && isSubmitting}
            className="flex-1 flex items-center justify-center gap-3 py-5 rounded-2xl font-extrabold text-xl tracking-tight transition-all bg-[#a855f7] hover:bg-[#9333ea] text-white disabled:opacity-60 disabled:cursor-not-allowed shadow-xl"
          >
            {isSubmitting ? (
              <>
                <span className="material-symbols-outlined animate-spin text-2xl" aria-hidden="true">refresh</span>
                {currentStep?.id === 'rootRedirect'
                  ? isEdit
                    ? 'Updating...'
                    : 'Preparing records...'
                  : 'Loading...'}
              </>
            ) : currentStep?.id === 'verify' ? (
              <>
                <span>Done</span>
                <span className="material-symbols-outlined text-2xl" aria-hidden="true">arrow_forward</span>
              </>
            ) : isEdit && currentStep?.id === 'rootRedirect' ? (
              <>
                <span>Update Root Redirect</span>
                <span className="material-symbols-outlined text-2xl" aria-hidden="true">check</span>
              </>
            ) : (
              <>
                <span>Next Step</span>
                <span className="material-symbols-outlined text-2xl" aria-hidden="true">arrow_forward</span>
              </>
            )}
          </button>
        </div>
      </footer>
    </div>
  );
}
